import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Clock, CalendarRange, TrendingUp,
  Palmtree, FileBarChart, Settings as Cog, Shield, ScrollText,
  User as UserIcon, LogOut, Menu, X, ClipboardList, ShieldCheck, Zap, Stethoscope,
  PanelLeftClose, PanelLeftOpen, UserCheck, HeartPulse, Sliders, ArrowRightLeft,
  UsersRound, Scale,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import UserAvatar from "@/components/UserAvatar";
import NotificationBell from "@/components/NotificationBell";

const rolesOrder = { employee: 20, manager: 40, hr_user: 60, admin: 80, super_admin: 100 };

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, min: "employee" },
  { to: "/my-hours", label: "My hours", icon: ClipboardList, min: "employee", employeeOnly: true },
  { to: "/time-tracking", label: "Time entry", icon: Clock, min: "manager" },
  { to: "/quick-hours", label: "Quick staff hours", icon: Zap, min: "manager" },
  { to: "/in-out", label: "In / Out entry", icon: UserCheck, min: "manager" },
  { to: "/weekly-hours", label: "Weekly hours", icon: CalendarRange, min: "manager" },
  { to: "/overtime", label: "Overtime", icon: TrendingUp, min: "manager" },
  { to: "/holidays", label: "Holidays", icon: Palmtree, min: "manager" },
  { to: "/sick-leave", label: "Sick leave", icon: Stethoscope, min: "manager" },
  { to: "/employees", label: "Employees", icon: Users, min: "manager" },
  { to: "/reports", label: "Reports", icon: FileBarChart, min: "manager" },
  { to: "/verify", label: "Verify report", icon: ShieldCheck, min: "hr_user" },
  { to: "/manager-assignments", label: "Manager assignments", icon: UsersRound, min: "admin" },
  { to: "/pension-check", label: "Pension check", icon: Scale, min: "admin" },
  { to: "/pension-rules", label: "Pension rules", icon: Sliders, min: "admin" },
  { to: "/pension-adjustments", label: "Pension adjustments", icon: ArrowRightLeft, min: "admin" },
  { to: "/insurance", label: "Insurance recon", icon: HeartPulse, min: "admin" },
  { to: "/users", label: "User accounts", icon: Shield, min: "admin" },
  { to: "/audit", label: "Audit log", icon: ScrollText, min: "admin" },
  { to: "/settings", label: "Settings", icon: Cog, min: "admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar:collapsed") === "1");

  useEffect(() => {
    setMobileOpen(false);
  }, []);

  // On every route change: close the mobile drawer and scroll the main pane to top.
  useEffect(() => {
    setMobileOpen(false);
    // Defer to next tick so the new page mounts before we scroll
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("sidebar:collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const canSee = (item) => {
    if (item.employeeOnly && user?.role !== "employee") return false;
    return (rolesOrder[user?.role] || 0) >= (rolesOrder[item.min] || 0);
  };

  const renderSidebar = (mini = false) => (
    <nav className={`flex flex-col gap-0.5 ${mini ? "px-2" : "px-3"} py-4`} data-testid="sidebar-nav">
      <div className={`${mini ? "px-1" : "px-3"} pb-5`}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-primary grid place-items-center text-primary-foreground font-display font-bold shrink-0">HR</div>
          {!mini && (
            <div>
              <div className="font-display font-bold text-base leading-none">HR Track</div>
              <div className="tiny-label mt-1">Workforce suite</div>
            </div>
          )}
        </div>
      </div>
      {NAV.filter(canSee).map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={`nav-${to.replace("/", "")}`}
          title={mini ? label : undefined}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `group flex items-center ${mini ? "justify-center px-2" : "gap-3 pl-3 pr-3"} py-2.5 rounded-md text-sm transition-colors border-l-[3px] ${
              isActive
                ? "bg-muted/60 border-primary text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            }`
          }
        >
          <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
          {!mini && label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-shrink-0 border-r border-border bg-card flex-col sticky top-0 h-screen app-scroll overflow-y-auto transition-[width] duration-200 ${collapsed ? "md:w-16" : "md:w-64"}`}>
        {renderSidebar(collapsed)}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative bg-card w-72 border-r border-border h-full app-scroll overflow-y-auto">
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} data-testid="close-sidebar">
                <X className="w-5 h-5" />
              </Button>
            </div>
            {renderSidebar(false)}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="open-sidebar">
              <Menu className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} data-testid="toggle-sidebar">
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </Button>
            <div className="hidden sm:block">
              <div className="tiny-label">Operations</div>
              <div className="font-display font-semibold text-lg leading-tight">Workforce console</div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell />
            <Badge variant="outline" className="hidden sm:inline-flex capitalize" data-testid="role-badge">{user?.role?.replace("_", " ")}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-1.5 pr-2" data-testid="user-menu-btn" data-test="user-menu-trigger" aria-label="user-menu">
                  <UserAvatar user={user} size={32} />
                  <span className="hidden sm:inline text-sm font-medium max-w-[160px] truncate">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-muted-foreground">Signed in as</div>
                  <div className="text-sm font-medium truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> Profile & 2FA
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="menu-change-password">
                  Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout" data-test="logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
